import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AgreementSubtype,
  CreateAgreementSubtypeInput,
  PaginatedResult,
  SortDirection,
  UpdateAgreementSubtypeInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface AgreementSubtypeListParams {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const agreementSubtypeKeys = {
  all: ['agreement-subtypes'] as const,
  list: (params: AgreementSubtypeListParams) => ['agreement-subtypes', 'list', params] as const,
};

export function useAgreementSubtypes(
  params: AgreementSubtypeListParams,
): UseQueryResult<PaginatedResult<AgreementSubtype>> {
  return useQuery({
    queryKey: agreementSubtypeKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<AgreementSubtype>>(
        `/agreement-subtypes${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          isActive: params.isActive,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

/** Convenience hook for selects: returns the active agreement subtypes ordered by sortOrder. */
export function useAgreementSubtypeOptions(): UseQueryResult<AgreementSubtype[]> {
  return useQuery({
    queryKey: ['agreement-subtypes', 'options'],
    queryFn: () => api.get<AgreementSubtype[]>('/agreement-subtypes/options'),
  });
}

export function useCreateAgreementSubtype(): UseMutationResult<
  AgreementSubtype,
  unknown,
  CreateAgreementSubtypeInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgreementSubtypeInput) =>
      api.post<AgreementSubtype>('/agreement-subtypes', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agreementSubtypeKeys.all });
    },
  });
}

export interface UpdateAgreementSubtypeArgs {
  id: string;
  input: UpdateAgreementSubtypeInput;
}

export function useUpdateAgreementSubtype(): UseMutationResult<
  AgreementSubtype,
  unknown,
  UpdateAgreementSubtypeArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateAgreementSubtypeArgs) =>
      api.patch<AgreementSubtype>(`/agreement-subtypes/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agreementSubtypeKeys.all });
    },
  });
}

export function useDeleteAgreementSubtype(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/agreement-subtypes/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agreementSubtypeKeys.all });
    },
  });
}
